import makeFeedbackApiRequest from '../utils/feedbackApi';
import makeWorkOrderApiRequest from '../utils/workOrderApi';
import {
  FeedbackScore,
  TimeLog,
  Question,
  QuestionsScore,
  MonoWorkOrderDetails,
} from './types.d';

type ApiError = { message?: string; [key: string]: unknown };

type FeedbackApiResponse<T> = {
  results?: T;
  errors?: ApiError[];
};

export const scoreRearrange = (
  scores: FeedbackScore[],
  qId: number,
  value: number,
): FeedbackScore[] => {
  const targetIndex = scores.findIndex((s) => s.id === qId);
  if (targetIndex !== -1) {
    // If the score exists, update the score
    const updatedScores = [...scores];
    updatedScores[targetIndex] = {
      id: qId,
      score: value,
    };
    return updatedScores;
  }
  // If the score doesn't exist, add a new score
  return [
    ...scores,
    {
      id: qId,
      score: value,
    },
  ];
};

export const getFeedbackQuestions = async (identifier: string): Promise<Question[]> => {
  try {
    const response = await makeFeedbackApiRequest<FeedbackApiResponse<Question[]>>({
      url: `/question/${identifier}`,
      method: 'GET',
    });
    if (response?.errors?.length) {
      return [];
    }
    return response?.results || [];
  } catch {
    return [];
  }
};

export const sendFeedback = async (payload: Record<string, unknown>): Promise<unknown> => {
  try {
    const response = await makeFeedbackApiRequest({
      url: '/feedback',
      method: 'POST',
      data: payload,
    });
    return response;
  } catch {
    return {};
  }
};

export const getFeedbackQuestionsScore = async (
  workOrderId: number,
  identifier: string,
): Promise<QuestionsScore> => {
  try {
    const response = await makeFeedbackApiRequest<FeedbackApiResponse<QuestionsScore>>({
      url: `/feedback/workorder/${workOrderId}`,
      method: 'GET',
      params: { identifier },
    });
    if (response?.errors?.length) {
      return {};
    }
    return response?.results || {};
  } catch {
    return {};
  }
};

export const getWorkOrderDetails = async (
  workOrderId: number,
): Promise<MonoWorkOrderDetails | null> => {
  try {
    const response = await makeWorkOrderApiRequest<MonoWorkOrderDetails>({
      url: `/${workOrderId}?columns=id,title,type_of_work,assignee,company,client,ratings,status,eta,problems,schedule,time_logs`,
      method: 'GET',
    });
    return response || null;
  } catch {
    return null;
  }
};

export const SUCCESS_MODAL = {
  TITLE: {
    PUBLIC: 'Your Public Review has been saved',
    PRIVATE: 'Your Private Feedback has been saved',
  },
  CONTENT: {
    PRIVATE: 'You can modify your Private Feedback any time up to 14 days after work order approval.',
  },
};

/**
 * Format late time from API response
 * @param {Object} firstCheckIn - The first check-in data with timeliness.minutes_late (in minutes)
 * @returns {string|null} - Formatted late time string
 * (e.g., "217 days 15 hr 36 mins late", "23 hr 26 mins late") or null
 */
export function calculateLateTime(
  firstCheckIn: TimeLog,
): string | null {
  const totalMinutes = Math.floor(firstCheckIn?.timeliness?.minutes_late || 0);
  if (!totalMinutes || totalMinutes <= 0) return null;

  const MINUTES_IN_DAY = 24 * 60;
  const days = Math.floor(totalMinutes / MINUTES_IN_DAY);
  const remainingAfterDays = totalMinutes % MINUTES_IN_DAY;
  const hours = Math.floor(remainingAfterDays / 60);
  const minutes = remainingAfterDays % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours} hr`);
  if (minutes > 0) parts.push(`${minutes} mins`);

  return parts.length ? `${parts.join(' ')} late` : null;
}
